/**
 * FathomVoyage — Maritime Pulse v3
 * SVG data backgrounds · Brief always visible · Morphing FAB share
 * Source attribution · Full brief in share text
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL     = import.meta.env.VITE_PULSE_API_URL || '/api/pulse';
const API_HISTORY = import.meta.env.VITE_PULSE_API_URL
  ? `${import.meta.env.VITE_PULSE_API_URL}/history`
  : '/api/pulse/history';

const track = (event, props = {}) => {
  if (window.__posthog) window.__posthog.capture(event, props);
};

const LOGIN_THRESHOLD  = 3;
const LOGIN_DISMISS_KEY = 'fv_login_dismissed_at';
const LOGIN_SHOWN_KEY   = 'fv_login_shown_count';
console.log('API URL:', import.meta.env.VITE_PULSE_API_URL);
// ─── CSS ──────────────────────────────────────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('fv-pulse3-css')) {
  const s = document.createElement('style');
  s.id = 'fv-pulse3-css';
  s.textContent = `
    .fv-story-overlay {
      position:fixed; inset:0; z-index:200;
      display:flex; flex-direction:column;
      touch-action:pan-y; overflow:hidden;
    }
    .fv-prog-track { height:2px; flex:1; border-radius:2px; background:rgba(255,255,255,0.22); overflow:hidden; }
    .fv-prog-fill  { height:100%; border-radius:2px; background:#fff; transform:scaleX(0); transform-origin:left; }
    .fv-prog-fill.run { transform:scaleX(1); transition:transform 7s linear; }
    .fv-story-card {
      flex:1; position:relative; display:flex; flex-direction:column;
      justify-content:flex-end; padding:0 22px 90px; user-select:none;
      overflow:hidden;
    }
    .fv-tap-l { position:absolute; top:0; bottom:0; left:0; width:35%; cursor:pointer; z-index:2; }
    .fv-tap-r { position:absolute; top:0; bottom:0; right:0; width:35%; cursor:pointer; z-index:2; }

    /* Morphing FAB */
    .fv-fab-wrap {
      position:absolute; bottom:28px; left:22px; z-index:10;
    }
    .fv-fab-main {
      width:48px; height:48px; border-radius:50%;
      background:rgba(255,255,255,0.18);
      border:1.5px solid rgba(255,255,255,0.35);
      display:flex; align-items:center; justify-content:center;
      cursor:pointer; backdrop-filter:blur(8px);
      transition:background 0.2s, transform 0.2s;
      position:relative; z-index:3;
    }
    .fv-fab-main:hover { background:rgba(255,255,255,0.28); }
    .fv-fab-main.open { transform:rotate(45deg); background:rgba(255,255,255,0.25); }
    .fv-fab-child {
      position:absolute; width:42px; height:42px; border-radius:50%;
      display:flex; align-items:center; justify-content:center;
      cursor:pointer; transition:all 0.3s cubic-bezier(0.34,1.56,0.64,1);
      opacity:0; transform:translate(0,0) scale(0.5);
      pointer-events:none; bottom:0; left:3px;
      border:none; z-index:2;
    }
    .fv-fab-wrap.open .fv-fab-child { opacity:1; pointer-events:all; }
    .fv-fab-wrap.open .fv-fab-child.c1 { transform:translate(0,-68px) scale(1); transition-delay:0.04s; }
    .fv-fab-wrap.open .fv-fab-child.c2 { transform:translate(46px,-52px) scale(1); transition-delay:0.08s; }
    .fv-fab-wrap.open .fv-fab-child.c3 { transform:translate(68px,-10px) scale(1); transition-delay:0.12s; }
    .fv-fab-label {
      position:absolute; right:50px; white-space:nowrap;
      background:rgba(0,0,0,0.7); color:#fff;
      font-size:11px; font-weight:600; padding:4px 9px; border-radius:6px;
      opacity:0; transition:opacity 0.2s; pointer-events:none;
    }
    .fv-fab-wrap.open .fv-fab-child:hover .fv-fab-label { opacity:1; }
    /* Copied toast */
    .fv-copied-toast {
      position:absolute; bottom:90px; left:22px;
      background:rgba(0,0,0,0.8); color:#fff;
      font-size:12px; padding:6px 14px; border-radius:20px;
      opacity:0; transition:opacity 0.3s; pointer-events:none;
    }
    .fv-copied-toast.show { opacity:1; }

    /* Desktop cards */
    .fv-pulse-strip { display:flex; gap:12px; overflow-x:auto; padding:4px 2px 16px; scrollbar-width:none; -webkit-overflow-scrolling:touch; }
    .fv-pulse-strip::-webkit-scrollbar { display:none; }
    .fv-pulse-card { flex-shrink:0; width:220px; border-radius:12px; overflow:hidden; border:0.5px solid var(--border,#E2E8F0); background:var(--card,#fff); cursor:pointer; transition:transform 0.15s,box-shadow 0.15s; }
    .fv-pulse-card:hover { transform:translateY(-3px); box-shadow:0 8px 24px rgba(15,23,42,0.1); }
    .fv-pulse-card.active { border:2px solid var(--teal,#e9a020); }
    .fv-pulse-detail { border-radius:12px; border:1px solid var(--border-mid,#CBD5E1); background:var(--card,#fff); margin-top:14px; overflow:hidden; max-height:0; transition:max-height 0.35s ease; }
    .fv-pulse-detail.open { max-height:700px; }

    /* Login prompt */
    .fv-login-prompt { position:fixed; bottom:0; left:0; right:0; z-index:150; padding:18px 20px 30px; background:var(--card,#fff); border-top:1px solid var(--border,#E2E8F0); border-radius:20px 20px 0 0; box-shadow:0 -8px 32px rgba(15,23,42,0.12); transform:translateY(100%); transition:transform 0.35s cubic-bezier(0.32,0.72,0,1); }
    .fv-login-prompt.show { transform:translateY(0); }

    @keyframes fvShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
    .fv-skeleton { background:linear-gradient(90deg,#111a2e 25%,#1a2540 50%,#111a2e 75%); background-size:200% 100%; animation:fvShimmer 1.5s infinite; border-radius:8px; }
    @media(min-width:769px) { .fv-mobile-only { display:none !important; } }
    @media(max-width:768px) { .fv-desktop-only { display:none !important; } }
    body.fv-story-mode nav { display:none !important; }
  `;
  document.head.appendChild(s);
}

// ─── Category config with SVG backgrounds ─────────────────────────────────────
const CAT = {
  'War Risk': {
    icon:'🛡', color:'#BE123C', bg:'#1A0509', accent:'#F43F5E', text:'#f87171',
    svgBg: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 700'%3E%3Ccircle cx='200' cy='620' r='120' fill='none' stroke='rgba(244,63,94,0.10)' stroke-width='1'/%3E%3Ccircle cx='200' cy='620' r='220' fill='none' stroke='rgba(244,63,94,0.07)' stroke-width='1'/%3E%3Ccircle cx='200' cy='620' r='320' fill='none' stroke='rgba(244,63,94,0.05)' stroke-width='1'/%3E%3Cline x1='0' y1='620' x2='400' y2='620' stroke='rgba(244,63,94,0.07)' stroke-width='0.5'/%3E%3Cline x1='200' y1='300' x2='200' y2='700' stroke='rgba(244,63,94,0.07)' stroke-width='0.5'/%3E%3Cline x1='50' y1='400' x2='350' y2='700' stroke='rgba(244,63,94,0.04)' stroke-width='0.5'/%3E%3Cline x1='350' y1='400' x2='50' y2='700' stroke='rgba(244,63,94,0.04)' stroke-width='0.5'/%3E%3C/svg%3E")`,
  },
  'Bunker Watch': {
    icon:'⛽', color:'#4338CA', bg:'#080718', accent:'#818CF8', text:'#818cf8',
    svgBg: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 700'%3E%3Crect x='30' y='480' width='18' height='120' fill='rgba(129,140,248,0.12)' rx='2'/%3E%3Crect x='30' y='460' width='18' height='20' fill='rgba(129,140,248,0.18)' rx='2'/%3E%3Crect x='70' y='520' width='18' height='80' fill='rgba(129,140,248,0.08)' rx='2'/%3E%3Crect x='70' y='500' width='18' height='20' fill='rgba(129,140,248,0.14)' rx='2'/%3E%3Crect x='110' y='440' width='18' height='160' fill='rgba(129,140,248,0.12)' rx='2'/%3E%3Crect x='110' y='420' width='18' height='20' fill='rgba(129,140,248,0.18)' rx='2'/%3E%3Crect x='150' y='500' width='18' height='100' fill='rgba(129,140,248,0.08)' rx='2'/%3E%3Crect x='150' y='480' width='18' height='20' fill='rgba(129,140,248,0.14)' rx='2'/%3E%3Crect x='190' y='460' width='18' height='140' fill='rgba(129,140,248,0.12)' rx='2'/%3E%3Crect x='190' y='440' width='18' height='20' fill='rgba(129,140,248,0.18)' rx='2'/%3E%3Crect x='230' y='510' width='18' height='90' fill='rgba(129,140,248,0.08)' rx='2'/%3E%3Crect x='230' y='490' width='18' height='20' fill='rgba(129,140,248,0.14)' rx='2'/%3E%3Crect x='270' y='430' width='18' height='170' fill='rgba(129,140,248,0.12)' rx='2'/%3E%3Crect x='270' y='410' width='18' height='20' fill='rgba(129,140,248,0.18)' rx='2'/%3E%3Crect x='310' y='490' width='18' height='110' fill='rgba(129,140,248,0.08)' rx='2'/%3E%3Crect x='310' y='470' width='18' height='20' fill='rgba(129,140,248,0.14)' rx='2'/%3E%3Crect x='350' y='450' width='18' height='150' fill='rgba(129,140,248,0.12)' rx='2'/%3E%3Crect x='350' y='430' width='18' height='20' fill='rgba(129,140,248,0.18)' rx='2'/%3E%3Cline x1='0' y1='600' x2='400' y2='600' stroke='rgba(129,140,248,0.08)' stroke-width='0.5'/%3E%3C/svg%3E")`,
  },
  'Route Intel': {
    icon:'🗺', color:'#065F46', bg:'#020F09', accent:'#34D399', text:'#34d399',
    svgBg: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 700'%3E%3Cpath d='M-20 600 Q100 500 200 520 Q300 540 420 400' fill='none' stroke='rgba(52,211,153,0.12)' stroke-width='2'/%3E%3Cpath d='M-20 650 Q80 550 200 570 Q320 590 420 460' fill='none' stroke='rgba(52,211,153,0.08)' stroke-width='1.5'/%3E%3Cpath d='M-20 550 Q120 450 200 470 Q280 490 420 350' fill='none' stroke='rgba(52,211,153,0.06)' stroke-width='1'/%3E%3Ccircle cx='200' cy='520' r='5' fill='rgba(52,211,153,0.25)'/%3E%3Ccircle cx='80' cy='570' r='4' fill='rgba(52,211,153,0.18)'/%3E%3Ccircle cx='320' cy='460' r='4' fill='rgba(52,211,153,0.18)'/%3E%3C/svg%3E")`,
  },
  'Freight Index': {
    icon:'📊', color:'#075985', bg:'#020C14', accent:'#38BDF8', text:'#38bdf8',
    svgBg: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 700'%3E%3Cpolyline points='0,640 50,620 100,630 150,580 200,560 250,540 300,510 350,480 400,440' fill='none' stroke='rgba(56,189,248,0.15)' stroke-width='2'/%3E%3Cpolyline points='0,680 50,660 100,670 150,620 200,600 250,580 300,550 350,510 400,470' fill='none' stroke='rgba(56,189,248,0.08)' stroke-width='1.5'/%3E%3Cline x1='0' y1='620' x2='400' y2='620' stroke='rgba(56,189,248,0.06)' stroke-width='0.5' stroke-dasharray='4,4'/%3E%3Cline x1='0' y1='560' x2='400' y2='560' stroke='rgba(56,189,248,0.05)' stroke-width='0.5' stroke-dasharray='4,4'/%3E%3Cline x1='0' y1='500' x2='400' y2='500' stroke='rgba(56,189,248,0.04)' stroke-width='0.5' stroke-dasharray='4,4'/%3E%3C/svg%3E")`,
  },
  'Port Watch': {
    icon:'⚓', color:'#B45309', bg:'#150900', accent:'#FBBF24', text:'#fbbf24',
    svgBg: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 700'%3E%3Ccircle cx='200' cy='480' r='60' fill='none' stroke='rgba(251,191,36,0.10)' stroke-width='1.5'/%3E%3Cline x1='200' y1='420' x2='200' y2='380' stroke='rgba(251,191,36,0.12)' stroke-width='2'/%3E%3Cline x1='160' y1='390' x2='240' y2='390' stroke='rgba(251,191,36,0.12)' stroke-width='2'/%3E%3Cline x1='140' y1='540' x2='200' y2='540' stroke='rgba(251,191,36,0.08)' stroke-width='1.5'/%3E%3Cline x1='260' y1='540' x2='200' y2='540' stroke='rgba(251,191,36,0.08)' stroke-width='1.5'/%3E%3Cline x1='140' y1='540' x2='120' y2='570' stroke='rgba(251,191,36,0.08)' stroke-width='1.5'/%3E%3Cline x1='260' y1='540' x2='280' y2='570' stroke='rgba(251,191,36,0.08)' stroke-width='1.5'/%3E%3Cline x1='0' y1='620' x2='400' y2='620' stroke='rgba(251,191,36,0.06)' stroke-width='0.5'/%3E%3Cline x1='0' y1='650' x2='400' y2='650' stroke='rgba(251,191,36,0.04)' stroke-width='0.5'/%3E%3C/svg%3E")`,
  },
  'Geopolitical': {
    icon:'🌍', color:'#334155', bg:'#080A0E', accent:'#94A3B8', text:'#94a3b8',
    svgBg: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 700'%3E%3Ccircle cx='200' cy='500' r='160' fill='none' stroke='rgba(148,163,184,0.08)' stroke-width='1'/%3E%3Cellipse cx='200' cy='500' rx='80' ry='160' fill='none' stroke='rgba(148,163,184,0.06)' stroke-width='1'/%3E%3Cellipse cx='200' cy='500' rx='160' ry='60' fill='none' stroke='rgba(148,163,184,0.06)' stroke-width='1'/%3E%3Cellipse cx='200' cy='500' rx='160' ry='110' fill='none' stroke='rgba(148,163,184,0.04)' stroke-width='0.5'/%3E%3Cline x1='40' y1='500' x2='360' y2='500' stroke='rgba(148,163,184,0.06)' stroke-width='0.5'/%3E%3C/svg%3E")`,
  },
  'Industry': {
    icon:'📰', color:'#334155', bg:'#080A0E', accent:'#94A3B8', text:'#94a3b8',
    svgBg: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 700'%3E%3Cline x1='40' y1='420' x2='360' y2='420' stroke='rgba(148,163,184,0.08)' stroke-width='0.5'/%3E%3Cline x1='40' y1='460' x2='280' y2='460' stroke='rgba(148,163,184,0.06)' stroke-width='0.5'/%3E%3Cline x1='40' y1='500' x2='320' y2='500' stroke='rgba(148,163,184,0.08)' stroke-width='0.5'/%3E%3Cline x1='40' y1='540' x2='240' y2='540' stroke='rgba(148,163,184,0.06)' stroke-width='0.5'/%3E%3Cline x1='40' y1='580' x2='300' y2='580' stroke='rgba(148,163,184,0.08)' stroke-width='0.5'/%3E%3C/svg%3E")`,
  },
};

const getCat = (category) => CAT[category] || CAT['Industry'];

// ─── Share helpers ─────────────────────────────────────────────────────────────
// Full brief included in share text
const buildShareText = (story, platform = 'whatsapp') => {
  if (platform === 'whatsapp') {
    return encodeURIComponent(
      `${story.icon || ''} *${story.category}*\n` +
      `${story.headline}\n\n` +
      `*${story.keyNumber}* — ${story.keyLabel}\n\n` +
      `${story.brief}\n\n` +
      `Source: ${story.source}\n` +
      `FathomVoyage: https://fathomvoyage.com`
    );
  }
  // LinkedIn — professional tone, no asterisks
  return encodeURIComponent(
    `${story.category}: ${story.headline}\n\n` +
    `${story.keyNumber} — ${story.keyLabel}\n\n` +
    `${story.brief}\n\n` +
    `Source: ${story.source} | FathomVoyage Maritime Intelligence\n` +
    `https://fathomvoyage.com`
  );
};

const waUrl  = (s) => `https://wa.me/?text=${buildShareText(s,'whatsapp')}`;
const liUrl  = (s) => `https://www.linkedin.com/feed/?shareActive=true&text=${buildShareText(s,'linkedin')}`;
const copyText = (s) =>
  `${s.category}: ${s.headline}\n\n${s.keyNumber} — ${s.keyLabel}\n\n${s.brief}\n\nSource: ${s.source} | https://fathomvoyage.com`;

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (d < 60)   return `${d}m ago`;
  if (d < 1440) return `${Math.floor(d/60)}h ago`;
  return `${Math.floor(d/1440)}d ago`;
};

const fmtUTC = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return String(d.getUTCHours()).padStart(2,'0') + ':' + String(d.getUTCMinutes()).padStart(2,'0');
};

// ─── Morphing FAB ──────────────────────────────────────────────────────────────
function MorphFAB({ story, onShare }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const toggle = (e) => { e.stopPropagation(); setOpen(o => !o); };

  const handleWa = (e) => {
    e.stopPropagation();
    track('story_shared_whatsapp', { story_id: story.id, category: story.category });
    onShare?.();
    window.open(waUrl(story), '_blank', 'noopener');
    setOpen(false);
  };
  const handleLi = (e) => {
    e.stopPropagation();
    track('story_shared_linkedin', { story_id: story.id, category: story.category });
    onShare?.();
    window.open(liUrl(story), '_blank', 'noopener');
    setOpen(false);
  };
  const handleCopy = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(copyText(story)); }
    catch { }
    track('story_copied', { story_id: story.id });
    setCopied(true);
    setTimeout(() => { setCopied(false); setOpen(false); }, 1800);
  };

  // Close on outside tap
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    setTimeout(() => document.addEventListener('click', close, { once: true }), 10);
  }, [open]);

  return (
    <div className={`fv-fab-wrap ${open ? 'open' : ''}`}>
      {/* Child: Copy link */}
      <button className="fv-fab-child c3" onClick={handleCopy}
        style={{ background:'#475569', boxShadow:'0 2px 8px rgba(0,0,0,0.4)' }}
        title="Copy full brief">
        <span className="fv-fab-label">Copy</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
          <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
        </svg>
      </button>

      {/* Child: LinkedIn */}
      <button className="fv-fab-child c2" onClick={handleLi}
        style={{ background:'#0A66C2', boxShadow:'0 2px 8px rgba(0,0,0,0.4)' }}
        title="Share on LinkedIn">
        <span className="fv-fab-label">LinkedIn</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      </button>

      {/* Child: WhatsApp */}
      <button className="fv-fab-child c1" onClick={handleWa}
        style={{ background:'#25D366', boxShadow:'0 2px 8px rgba(0,0,0,0.4)' }}
        title="Share on WhatsApp">
        <span className="fv-fab-label">WhatsApp</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </button>

      {/* Main toggle button */}
      <button className={`fv-fab-main ${open ? 'open' : ''}`} onClick={toggle}>
        {open ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
        )}
      </button>

      {/* Copied toast */}
      <div className={`fv-copied-toast ${copied ? 'show' : ''}`}>Copied to clipboard</div>
    </div>
  );
}

// ─── Mobile story player ───────────────────────────────────────────────────────
function StoryPlayer({ stories, initialIdx, onClose, onEngagement }) {
  const [idx,      setIdx]    = useState(initialIdx);
  const [progKey,  setProgKey]= useState(0);
  const [paused,   setPaused] = useState(false);
  const navigate   = useNavigate();
  const timerRef   = useRef(null);
  const holdRef    = useRef(null);
  const touchRef   = useRef(null);

  const story = stories[idx];
  const cat   = getCat(story?.category);

  const goNext = useCallback(() => {
    if (idx < stories.length - 1) setIdx(i => i + 1);
    else onClose();
  }, [idx, stories.length, onClose]);

  const goPrev = () => { if (idx > 0) setIdx(i => i - 1); };

  useEffect(() => {
    setProgKey(k => k + 1);
    if (story) {
      track('story_viewed', { story_id: story.id, category: story.category });
      onEngagement();
    }
  }, [idx]);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!paused) timerRef.current = setTimeout(goNext, 7000);
    return () => clearTimeout(timerRef.current);
  }, [idx, paused, goNext]);

  const onTouchStart = (e) => {
    touchRef.current = { y: e.touches[0].clientY, t: Date.now() };
    holdRef.current = setTimeout(() => setPaused(true), 150);
  };
  const onTouchEnd = (e) => {
    clearTimeout(holdRef.current);
    setPaused(false);
    touchRef.current = null;
  };

  if (!story) return null;

  return (
    <div className="fv-story-overlay"
      style={{ background: cat.bg, backgroundImage: cat.svgBg, backgroundSize: 'cover', backgroundPosition: 'center bottom' }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Progress + header */}
      <div style={{ padding:'50px 16px 10px', zIndex:5, position:'relative' }}>
        <div style={{ display:'flex', gap:4, marginBottom:14 }}>
          {stories.map((_,i) => (
            <div key={i} className="fv-prog-track">
              <div key={`${i}-${progKey}`}
                className={`fv-prog-fill ${i === idx && !paused ? 'run' : ''}`}
                style={{ transform: i < idx ? 'scaleX(1)' : undefined }}
              />
            </div>
          ))}
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:28, height:28, borderRadius:8, background:'rgba(233,160,32,0.20)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ fontSize:13, color:'#e9a020', fontWeight:700, fontFamily:'monospace' }}>F</span>
            </div>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:'#fff', letterSpacing:'-0.3px' }}>
                Fathom<span style={{ color:'#e9a020' }}>Voyage</span>
              </div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.75)' }}>
                {story.source} · {fmtTime(story.publishedAt)}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ width:32, height:32, borderRadius:'50%', background:'rgba(255,255,255,0.12)', border:'none', color:'#fff', fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
        </div>
      </div>

      {/* Tap zones */}
      <div className="fv-tap-l" onClick={goPrev}/>
      <div className="fv-tap-r" onClick={goNext}/>

      {/* Story content */}
      <div className="fv-story-card">
        {/* Category badge */}
        <div style={{ display:'inline-flex', alignItems:'center', gap:6, marginBottom:12, padding:'5px 12px', borderRadius:20, background:`${cat.accent}22`, border:`1px solid ${cat.accent}45`, width:'fit-content' }}>
          <span style={{ fontSize:14 }}>{cat.icon}</span>
          <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.8px', color:cat.accent }}>{story.category}</span>
        </div>

        {/* Key number */}
        <div style={{ fontFamily:'var(--font-mono,monospace)', fontSize:'clamp(40px,11vw,58px)', fontWeight:500, color:cat.accent, letterSpacing:'-2px', lineHeight:1, marginBottom:4 }}>
          {story.keyNumber}
        </div>
        <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', marginBottom:14, fontFamily:'var(--font-mono,monospace)' }}>
          {story.keyLabel}
        </div>

        {/* Headline */}
        <div style={{ fontSize:'clamp(16px,4.5vw,20px)', fontWeight:500, color:'#fff', lineHeight:1.35, marginBottom:12 }}>
          {story.headline}
        </div>

        {/* Brief — always visible, no tap needed */}
        <div style={{ fontSize:'clamp(12px,3.2vw,14px)', color:'rgba(255,255,255,0.65)', lineHeight:1.65, marginBottom:16 }}>
          {story.brief}
        </div>

        {/* Module link */}
        {story.moduleLink && (
          <button
            onClick={(e) => { e.stopPropagation(); track('story_module_click', { story_id:story.id, destination:story.moduleLink }); onClose(); navigate(story.moduleLink); }}
            style={{ alignSelf:'flex-start', padding:'8px 14px', borderRadius:20, background:`${cat.accent}20`, border:`1px solid ${cat.accent}45`, color:cat.accent, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit', marginBottom:8, zIndex:3, position:'relative' }}>
            Open {story.moduleName} →
          </button>
        )}

        {/* Watermark */}
        <div style={{ position:'absolute', bottom:32, right:22, opacity:0.2, fontSize:11, fontFamily:'monospace', color:'#fff', letterSpacing:'0.5px', zIndex:1 }}>
          fathomvoyage.com
        </div>
      </div>

      {/* Morphing FAB — bottom-left */}
      <MorphFAB story={story} onShare={onEngagement}/>
    </div>
  );
}

// ─── Desktop card strip ────────────────────────────────────────────────────────
function DesktopStrip({ stories, loading, onEngagement }) {
  const [activeId, setActiveId] = useState(null);
  const navigate = useNavigate();
  const [copied,   setCopied]  = useState(false);
  const active = stories.find(s => s.id === activeId);

  const toggle = (story) => {
    const newId = activeId === story.id ? null : story.id;
    setActiveId(newId);
    if (newId) {
      track('story_expanded', { story_id: story.id, category: story.category });
      onEngagement();
    }
  };

  const handleCopy = async (story) => {
    try { await navigator.clipboard.writeText(copyText(story)); }
    catch {}
    track('story_copied', { story_id: story.id });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div style={{ display:'flex', gap:12, padding:'4px 2px 16px' }}>
      {[1,2,3,4].map(i => (
        <div key={i} style={{ flexShrink:0, width:220, borderRadius:12, border:'0.5px solid var(--border,#E2E8F0)', overflow:'hidden' }}>
          <div style={{ height:3, background:'var(--border,#1e2d44)' }}/>
          <div style={{ padding:14, display:'flex', flexDirection:'column', gap:8 }}>
            <div className="fv-skeleton" style={{ height:10, width:80 }}/>
            <div className="fv-skeleton" style={{ height:28, width:100 }}/>
            <div className="fv-skeleton" style={{ height:10, width:120 }}/>
            <div style={{ height:0.5, background:'var(--border,#1e2d44)', margin:'4px 0' }}/>
            <div className="fv-skeleton" style={{ height:12, width:'100%' }}/>
            <div className="fv-skeleton" style={{ height:12, width:'80%' }}/>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <div className="fv-pulse-strip">
        {stories.map(story => {
          const cat = getCat(story.category);
          return (
            <div key={story.id} className={`fv-pulse-card ${activeId === story.id ? 'active' : ''}`} onClick={() => toggle(story)}>
              <div style={{ height:3, background:cat.color }}/>
              <div style={{ padding:'14px 14px 12px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10 }}>
                  <span style={{ fontSize:14 }}>{cat.icon}</span>
                  <span style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.7px', color:cat.text }}>{story.category}</span>
                </div>
                <div style={{ fontFamily:'var(--font-mono,monospace)', fontSize:26, fontWeight:500, color:cat.text, letterSpacing:'-1px', lineHeight:1, marginBottom:4 }}>
                  {story.keyNumber}
                </div>
                <div style={{ fontSize:11, color:'var(--text-3,#94A3B8)', fontFamily:'var(--font-mono,monospace)', marginBottom:10 }}>
                  {story.keyLabel}
                </div>
                <div style={{ height:0.5, background:'var(--border,#E2E8F0)', marginBottom:10 }}/>
                <div style={{ fontSize:13, fontWeight:500, color:'var(--text,#0F172A)', lineHeight:1.4, marginBottom:8 }}>
                  {story.headline}
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:11, color:'var(--text-3,#94A3B8)' }}>via {story.source} · {fmtTime(story.publishedAt)}</span>
                  <span style={{ fontSize:11, fontWeight:600, color:cat.text }}>{activeId === story.id ? '▲ close' : '▼ expand'}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Expanded detail */}
      <div className={`fv-pulse-detail ${activeId ? 'open' : ''}`}>
        {active && (() => {
          const cat = getCat(active.category);
          return (
            <div>
              <div style={{ padding:'16px 20px', background:cat.bg, backgroundImage:cat.svgBg, backgroundSize:'cover', backgroundPosition:'center', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
                <div>
                  <div style={{ display:'inline-flex', alignItems:'center', gap:6, marginBottom:10, padding:'4px 10px', borderRadius:20, background:`${cat.accent}20`, border:`1px solid ${cat.accent}40` }}>
                    <span style={{ fontSize:13 }}>{cat.icon}</span>
                    <span style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.7px', color:cat.accent }}>{active.category}</span>
                  </div>
                  <div style={{ fontFamily:'var(--font-mono,monospace)', fontSize:32, fontWeight:500, color:cat.accent, letterSpacing:'-1.5px', lineHeight:1, marginBottom:6 }}>
                    {active.keyNumber}
                  </div>
                  <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', fontFamily:'var(--font-mono,monospace)' }}>{active.keyLabel}</div>
                </div>
                <div style={{ fontSize:17, fontWeight:500, color:'#fff', lineHeight:1.35, maxWidth:'55%', textAlign:'right' }}>{active.headline}</div>
              </div>
              <div style={{ padding:'18px 20px' }}>
                <div style={{ fontSize:14, color:'var(--text-2,#334155)', lineHeight:1.75, marginBottom:18 }}>{active.brief}</div>
                <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
                  {/* Share row: three icons */}
                  <a href={waUrl(active)} target="_blank" rel="noopener noreferrer"
                    onClick={() => { track('story_shared_whatsapp', { story_id:active.id }); onEngagement(); }}
                    style={{ width:38, height:38, borderRadius:'50%', background:'#25D366', display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none' }}
                    title="Share on WhatsApp">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </a>
                  <a href={liUrl(active)} target="_blank" rel="noopener noreferrer"
                    onClick={() => { track('story_shared_linkedin', { story_id:active.id }); onEngagement(); }}
                    style={{ width:38, height:38, borderRadius:'50%', background:'#0A66C2', display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none' }}
                    title="Share on LinkedIn">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  </a>
                  <button onClick={() => handleCopy(active)}
                    style={{ width:38, height:38, borderRadius:'50%', background:copied?'#059669':'var(--bg,#F1F5F9)', border:'1px solid var(--border-mid,#CBD5E1)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'background 0.2s' }}
                    title={copied ? 'Copied!' : 'Copy full brief'}>
                    {copied
                      ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                      : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-2,#334155)" strokeWidth="2" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                    }
                  </button>

                  {active.moduleLink && (
                    <button onClick={() => navigate(active.moduleLink)}
                      style={{ padding:'10px 16px', borderRadius:50, background:'var(--bg,#F1F5F9)', border:'1px solid var(--border-mid,#CBD5E1)', color:'var(--text,#0F172A)', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                      Open {active.moduleName} →
                    </button>
                  )}
                  <span style={{ fontSize:12, color:'var(--text-3,#94A3B8)', marginLeft:'auto' }}>
                    {active.source} · {fmtTime(active.publishedAt)}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ─── Login prompt ─────────────────────────────────────────────────────────────
function LoginPrompt({ show, onDismiss }) {
  return (
    <div className={`fv-login-prompt ${show ? 'show' : ''}`}>
      <button onClick={onDismiss} style={{ position:'absolute', top:12, right:16, width:28, height:28, borderRadius:'50%', background:'var(--bg,#F1F5F9)', border:'none', fontSize:16, cursor:'pointer', color:'var(--text-2,#334155)', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
      <div style={{ maxWidth:480, margin:'0 auto' }}>
        <div style={{ fontFamily:'var(--font-display,sans-serif)', fontSize:16, fontWeight:600, color:'var(--text,#0F172A)', marginBottom:6 }}>Save your preferences</div>
        <div style={{ fontSize:14, color:'var(--text-2,#334155)', marginBottom:16, lineHeight:1.6 }}>
          Sign in with LinkedIn to save your vessel preferences and route calculations across sessions.
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          <button style={{ display:'flex', alignItems:'center', gap:8, padding:'11px 20px', borderRadius:8, background:'#0A66C2', border:'none', color:'#fff', fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            Continue with LinkedIn
          </button>
          <button onClick={onDismiss} style={{ padding:'11px 16px', borderRadius:8, border:'1px solid var(--border-mid,#CBD5E1)', background:'var(--card,#fff)', color:'var(--text-2,#334155)', fontSize:14, cursor:'pointer', fontFamily:'inherit' }}>
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function PulseModule() {
  const [stories,         setStories]        = useState([]);
  const [history,         setHistory]        = useState([]);
  const [loading,         setLoading]        = useState(true);
  const [isMock,          setIsMock]         = useState(false);
  const [storyOpen,       setStoryOpen]      = useState(false);
  const [storyIdx,        setStoryIdx]       = useState(0);
  const [loginShow,       setLoginShow]      = useState(false);
  const [showHist,        setShowHist]       = useState(false);
  const [mobileIndex,     setMobileIndex]    = useState(0);
  const [touchStart,      setTouchStart]     = useState(null);
  const [showOlderMobile, setShowOlderMobile] = useState(false);
  const engRef = useRef(0);

  useEffect(() => {
    const ctrl = new AbortController();
    Promise.all([
      fetch(API_URL, { signal:ctrl.signal }).then(r=>r.json()).catch(()=>null),
      fetch(API_HISTORY, { signal:ctrl.signal }).then(r=>r.json()).catch(()=>[]),
    ]).then(([current, hist]) => {
      if (current?.stories?.length) {
        setStories(current.stories);
        setIsMock(!!current.isMock);
        setMobileIndex(0);
        setShowOlderMobile(false);
      }
      if (Array.isArray(hist) && hist.length) {
        const flat = hist.flatMap(b => b.stories || [])
          .filter((s,i,a) => a.findIndex(x=>x.id===s.id)===i)
          .slice(0, 60);
        setHistory(flat);
      }
    }).finally(() => setLoading(false));
    return () => ctrl.abort();
  }, []);

  const handleEngagement = useCallback(() => {
    engRef.current += 1;
    const dismissedAt = localStorage.getItem(LOGIN_DISMISS_KEY);
    if (dismissedAt && Date.now() - parseInt(dismissedAt) < 86400000) return;
    if (engRef.current >= LOGIN_THRESHOLD) {
      const shown = parseInt(localStorage.getItem(LOGIN_SHOWN_KEY)||'0');
      if (shown < 3) { setLoginShow(true); localStorage.setItem(LOGIN_SHOWN_KEY, String(shown+1)); }
    }
  }, []);

  const dismissLogin = () => { setLoginShow(false); localStorage.setItem(LOGIN_DISMISS_KEY, String(Date.now())); };

  const openStory = (idx=0) => {
    setStoryIdx(idx); setStoryOpen(true);
    document.body.classList.add('fv-story-mode');
    document.body.style.overflow = 'hidden';
    track('story_session_started', { start_index:idx });
  };
  const closeStory = () => {
    setStoryOpen(false);
    document.body.classList.remove('fv-story-mode');
    document.body.style.overflow = '';
  };
  useEffect(() => () => { document.body.classList.remove('fv-story-mode'); document.body.style.overflow=''; }, []);

  const historyByDay = useMemo(() => {
    const groups = {};
    history.forEach(s => {
      const day = new Date(s.publishedAt).toLocaleDateString('en-GB', { day:'numeric', month:'short' });
      if (!groups[day]) groups[day] = [];
      groups[day].push(s);
    });
    return Object.entries(groups).slice(0, 14);
  }, [history]);

  const latestAt = useMemo(() =>
    stories.length > 0
      ? stories.reduce((a, b) => new Date(b.publishedAt) > new Date(a.publishedAt) ? b : a).publishedAt
      : null,
  [stories]);

  const mobileStories = useMemo(() =>
    showOlderMobile
      ? [...stories, ...history.filter(h => !stories.some(s => s.id === h.id))]
      : stories,
  [stories, history, showOlderMobile]);

  return (
    <div>
      {storyOpen && stories.length > 0 && (
        <div className="fv-mobile-only">
          <StoryPlayer stories={stories} initialIdx={storyIdx} onClose={closeStory} onEngagement={handleEngagement}/>
        </div>
      )}

      <LoginPrompt show={loginShow} onDismiss={dismissLogin}/>

      {/* Header */}
      <div style={{ background:'var(--card,#fff)', padding:'clamp(12px,2vw,18px) clamp(16px,2.5vw,28px)', borderBottom:'1px solid var(--border,#E2E8F0)', boxShadow:'0 1px 3px rgba(15,23,42,0.06)' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ fontFamily:'var(--font-sans,sans-serif)', fontSize:12, textTransform:'uppercase', letterSpacing:'1.5px', color:'var(--teal,#e9a020)', fontWeight:700, marginBottom:4 }}>Maritime Pulse</div>
            <div style={{ fontFamily:'var(--font-display,sans-serif)', fontSize:'clamp(17px,1.8vw,22px)', fontWeight:700, color:'var(--text,#0F172A)', letterSpacing:'-0.4px' }}>Shipping intelligence, updated every 6 hours</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {loading && (
              <span style={{ fontSize:12, color:'var(--text-3,#94A3B8)', fontFamily:'var(--font-sans,sans-serif)' }}>Compiling briefing...</span>
            )}
            {!loading && !isMock && (
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:'#10B981', animation:'livepulse 2s infinite' }}/>
                <span style={{ fontSize:12, color:'var(--text-3,#94A3B8)', fontFamily:'var(--font-mono,monospace)' }}>
                  {latestAt ? `Last updated ${fmtUTC(latestAt)} UTC` : 'Live'}
                </span>
              </div>
            )}
            {isMock && <span style={{ fontSize:12, padding:'3px 10px', borderRadius:20, background:'var(--amber-light,#FEF3C7)', color:'var(--amber,#B45309)', fontWeight:700, border:'1px solid var(--amber-mid)' }}>DEMO</span>}
          </div>
        </div>
      </div>

      <div style={{ padding:'clamp(16px,2vw,28px)' }}>

        {/* Mobile: single-story carousel */}
        <div className="fv-mobile-only">
          {loading ? (
            <div className="fv-skeleton" style={{ borderRadius:12, aspectRatio:'9/14', width:'100%' }}/>
          ) : (
            <>
              <div className="pm-mobile-carousel">
                <button className="pm-carousel-arrow left"
                  onClick={() => setMobileIndex(i => Math.max(0, i-1))}
                  style={{ opacity: mobileIndex === 0 ? 0.3 : 1 }}>‹</button>
                <button className="pm-carousel-arrow right"
                  onClick={() => setMobileIndex(i => Math.min(mobileStories.length-1, i+1))}
                  style={{ opacity: mobileIndex === mobileStories.length-1 ? 0.3 : 1 }}>›</button>
                <div className="pm-carousel-track"
                  style={{ transform: `translateX(-${mobileIndex * 100}%)` }}
                  onTouchStart={(e) => setTouchStart(e.touches[0].clientX)}
                  onTouchEnd={(e) => {
                    if (touchStart === null) return;
                    const delta = e.changedTouches[0].clientX - touchStart;
                    if (delta < -50) setMobileIndex(i => Math.min(mobileStories.length-1, i+1));
                    if (delta > 50)  setMobileIndex(i => Math.max(0, i-1));
                    setTouchStart(null);
                  }}
                >
                  {mobileStories.map((story, i) => {
                    const cat = getCat(story.category);
                    return (
                      <div key={story.id} className="pm-carousel-card"
                        onClick={() => { if (i < stories.length) openStory(i); }}
                      >
                        <div style={{ borderRadius:12, overflow:'hidden', cursor:'pointer', aspectRatio:'9/14', position:'relative', background:cat.bg, backgroundImage:cat.svgBg, backgroundSize:'cover', backgroundPosition:'center bottom' }}>
                          <div style={{ position:'absolute', inset:0, padding:'12px 12px 14px', display:'flex', flexDirection:'column', justifyContent:'flex-end', background:'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 50%)' }}>
                            <div style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.6px', color:cat.accent, marginBottom:4 }}>
                              {cat.icon} {story.category}
                            </div>
                            <div style={{ fontFamily:'var(--font-mono,monospace)', fontSize:22, fontWeight:500, color:cat.accent, letterSpacing:'-1px', lineHeight:1, marginBottom:4 }}>
                              {story.keyNumber}
                            </div>
                            <div style={{ fontSize:12, color:'#fff', lineHeight:1.3, fontWeight:500 }}>
                              {story.headline.length>55 ? story.headline.slice(0,55)+'…' : story.headline}
                            </div>
                            <div style={{ fontSize:12, color:'rgba(255,255,255,0.45)', marginTop:4 }}>via {story.source}</div>
                          </div>
                          {i < stories.length && (
                            <div style={{ position:'absolute', top:8, right:10, fontSize:12, color:'rgba(255,255,255,0.35)' }}>▶</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="pm-carousel-nav">
                {mobileStories.slice(0, 7).map((_, i) => (
                  <div key={i} className={`pm-carousel-dot${mobileIndex === i ? ' active' : ''}`}
                    onClick={() => setMobileIndex(i)}/>
                ))}
                {mobileStories.length > 7 && (
                  <span style={{ fontSize:11, color:'var(--text2)', fontFamily:'var(--font-sans)' }}>+</span>
                )}
              </div>

              {!showOlderMobile && history.length > 0 && (
                <button className="pm-load-earlier" onClick={() => setShowOlderMobile(true)}>
                  Load earlier briefings →
                </button>
              )}
            </>
          )}
        </div>

        {/* Desktop: card strip */}
        <div className="fv-desktop-only">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:10 }}>
            <div style={{ fontSize:'clamp(13px,1.1vw,15px)', color:'var(--text-2,#334155)', fontWeight:500 }}>
              Click to expand · share via WhatsApp, LinkedIn, or copy
            </div>
            <div style={{ fontSize:13, color:'var(--text-3,#94A3B8)' }}>← scroll for more →</div>
          </div>
          <DesktopStrip stories={stories} loading={loading} onEngagement={handleEngagement}/>
        </div>

        {/* Historical stories */}
        {historyByDay.length > 0 && (
          <div style={{ marginTop:28 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
              <div>
                <div style={{ fontFamily:'var(--font-display,sans-serif)', fontSize:'clamp(14px,1.2vw,16px)', fontWeight:600, color:'var(--text,#0F172A)' }}>Previous briefings</div>
                <div style={{ fontSize:13, color:'var(--text-3,#94A3B8)', marginTop:2 }}>Last 30 days</div>
              </div>
              <button onClick={()=>setShowHist(h=>!h)} style={{ padding:'7px 14px', borderRadius:8, border:'1px solid var(--border-mid,#CBD5E1)', background:'var(--card,#fff)', color:'var(--text-2,#334155)', fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
                {showHist ? '▲ Hide' : '▼ Show previous'}
              </button>
            </div>
            {showHist && historyByDay.map(([day, dayStories]) => (
              <div key={day} style={{ marginBottom:20 }}>
                <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'1px', color:'var(--muted,#94A3B8)', marginBottom:10, padding:'6px 0', borderBottom:'1px solid var(--border,#E2E8F0)' }}>{day}</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {dayStories.map(story => {
                    const cat = getCat(story.category);
                    return (
                      <div key={story.id} style={{ display:'flex', gap:12, alignItems:'center', padding:'10px 14px', borderRadius:10, border:'0.5px solid var(--border,#E2E8F0)', background:'var(--card,#fff)', opacity:0.85 }}>
                        <span style={{ fontSize:16, flexShrink:0 }}>{cat.icon}</span>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.6px', color:cat.text, marginBottom:2 }}>{story.category}</div>
                          <div style={{ fontSize:13, fontWeight:500, color:'var(--text,#0F172A)', lineHeight:1.35, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{story.headline}</div>
                        </div>
                        <div style={{ fontFamily:'var(--font-mono,monospace)', fontSize:15, fontWeight:600, color:cat.text, flexShrink:0, textAlign:'right' }}>
                          {story.keyNumber}
                          <div style={{ fontSize:12, color:'var(--text-3,#94A3B8)', fontWeight:400 }}>{fmtTime(story.publishedAt)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="pm-source-footer">
          Sources: UKMTO · Ship &amp; Bunker · Baltic Exchange · Hellenic Shipping News · TradeWinds · Splash247 · Safety4Sea · Argus · BIMCO
        </div>
      </div>
    </div>
  );
}
